import { ENV } from "@/axios/constants";
import {
	ensureFreshToken,
	handleAuthError,
} from "./features/auth/useRefreshAccessToken";
// import ReferredFriendsPage from "./pages/ReferredFriends/index";
import { getUserInformationOptions } from "@/features/auth/useGetUserInformation";
import { getStatesQueryOptions } from "@/features/states/useGetStates";
import FaceRegistration from "@/pages/SelfieRegistration";
import VerifyPhoneCodePage from "@/pages/SignIn/VerifyPhoneCodePage";
import { queryClient } from "@/query-client";
import LoadingScreen from "@/components/_module/loading";
import { QueryClient } from "@tanstack/react-query";
import {
	Outlet,
	createRootRouteWithContext,
	createRoute,
	createRouter,
	redirect,
	useRouterState,
} from "@tanstack/react-router";
import { z } from "zod";
import { LocationByDomainSchema } from "./api/location-settings";
import { setLocationIdForRequests } from "./axios/interceptors";
import ErrorFallback from "./components/ErrorFallback";
import AuthWrapper from "./components/AuthWrapper";
import LocationSettingsSetup from "./components/LocationSettingsSetup";
import SignUpAuthWrapper from "./components/SignUpAuthWrapper";
import UserAccountSetupGuard from "./components/UserAccountSetupGuard";
import UserVerifiedAuthGuard from "./components/_module/userVerifiedAuthGuard/userVerifiedAuthGuard";
import PageTransition from "./components/ui/PageTransition";
import IntercomChatGuest from "./components/ui/intercom/IntercomChatGuest";
import IntercomUserChat from "./components/ui/intercom/IntercomUserChat";
import { toast } from "./components/ui/use-toast";
import { UserAgreementGate } from "./components/user-agreement-signing/UserAgreementGate";
import { US_STATES_ID } from "./constants/common-constants";
import { bankAccountsQueryOptions } from "./features/bank-accounts/useGetBankAccounts";
import { creditCardsQueryOptions } from "./features/credit-cards/useGetCreditCards";
import { discountCodeByUserQuery } from "./features/discount_codes/useGetDiscountCodeByUser";
import { locationSettingsKeys } from "./features/location-settings/keys";
import { getDomainLocationSettingsQueryOptions } from "./features/location-settings/useGetDomainLocationSettings";
import { paymentCredentialsOptions } from "./features/payment-credentials/useGetPaymentCredentials";
import ResumePendingTransferIn from "./features/transactions/ResumePendingTransferIn";
import ClearSessionErrorCheck from "./features/identity-verification/ClearSessionErrorCheck";
import { transactionsQueryOptions } from "./features/transactions/useGetTransactions";
import { documentSigningEventTypesQueryOptions } from "./features/user-agreement/useDocumentSigningEvents";
import AuthenticatedGuestLayout from "./layout/AuthenticatedGuestLayout";
import { applyApplicationTheme } from "./lib/color-palette/color-palette";
import EmailVerificationPage from "./pages/EmailVerification/emailVerification";
import HomeOverviewPage from "./pages/Home";
import ActivityPage from "./pages/Home/activity";
import CardViewPage from "./pages/Home/card";
import NewCardPage from "./pages/Home/card/new";
import CardViewTestPage from "./pages/Home/card/view";
import DepositPage from "./pages/Home/deposit";
import DepositAmountPage from "./pages/Home/deposit/amount";
import OnboardingPage from "./pages/Home/onboarding";
import SettingPage from "./pages/Home/setting";
import BusinessDetailsCreatePage from "./pages/Home/setting/business-details/create";
import BusinessDetailsEditPage from "./pages/Home/setting/business-details/edit";
import BusinessDetailsIndexPage from "./pages/Home/setting/business-details/index";
import LinkedAccountsPage from "./pages/Home/setting/linked-accounts";
import NewAccountPage from "./pages/Home/setting/linked-accounts/new/index";
import VerifyBankLinkingPage from "./pages/Home/setting/linked-accounts/new/verify";
import PersonalInfoSettingsPage from "./pages/Home/setting/personalInfo/index";
import SecuritySettingsLayout from "./pages/Home/setting/security/_components/SecuritySettingsLayout";
import ChangePasswordPage from "./pages/Home/setting/security/change-password";
import ChangePinPage from "./pages/Home/setting/security/change-pin";
import SecurityChecklistPage from "./pages/Home/setting/security/checklist";
import { SecurityPage } from "./pages/Home/setting/security/index";
import VerifiedPage from "./pages/Home/verified";
import WithdrawPage from "./pages/Home/widthdraw";
import WithdrawAmountPage from "./pages/Home/widthdraw/amount";
import ValidateVerificationPage from "./pages/IdentityVerification/validate-verification";
import VerifyIdentityPage from "./pages/IdentityVerification/verify";
import LoadingPage from "./pages/Loading";
import NotFound from "./pages/NotFound";
import PasswordRecoveryPage from "./pages/PasswordRecovery/passwordRecovery";
import ResetPasswordPage from "./pages/ResetPassword/resetPassword";
import FaceIDPage from "./pages/SignIn";
import FaceSignInPage from "./pages/SignIn/faceId";
import SignInMailPage from "./pages/SignIn/signInMail";
import SignPinPage from "./pages/SignIn/signPin";
import SignUpWrapper from "./pages/SignUp/SignUpWrapper";
import VerifyAccountPage from "./pages/SignUp/VerifyAccountPage";
import WelcomePage from "./pages/Welcome";
import WelcomeMessagePage from "./pages/Welcome/message";
// import ReferredFriendsPage from "./pages/ReferredFriends/index";
import { guestSignatureStatusQueryOptions } from "./api/user-agreement-document";
import { MaintenanceGate } from "./components/MaintenanceGate";
import {
	clearLocationSettingsFromStorage,
	getLocationSettingsFromStorage,
	setLocationSettingsInStorage,
} from "./utils/location-settings";
import {
	type PromoCodeSearch,
	validatePromoCodeSearch,
} from "./utils/promo-code";

// -----------------------------------------------------------------------------
// Site maintenance: set MAINTENANCE_MODE_ENABLED to true to show a full-screen
// maintenance page for all routes. Bypass for this browser tab only by entering
// MAINTENANCE_BYPASS_PASSWORD in the on-page input (change the password before use).
// -----------------------------------------------------------------------------
const MAINTENANCE_MODE_ENABLED = false;
const MAINTENANCE_BYPASS_PASSWORD = "CHANGE_ME_BEFORE_ENABLING_MAINTENANCE";

// Kashu and the White Label apps depend on the domain to fetch location settings.
function getDomain(): string {
	const isLocalhost = window.location.hostname === "localhost";

	// For testing in Android emulator
	const isLocalIp = window.location.hostname.includes("192.168");

	// In local development environments, the backend can't determine which location settings to use
	// based on the domain alone. Use the VITE_LOCALHOST_DOMAIN environment variable to specify
	// which domain's settings should be loaded during local testing.
	if (isLocalhost || isLocalIp) {
		return ENV.VITE_LOCALHOST_DOMAIN || "https://dev-kashu.tabpoint.us";
	}

	// For deployed environments, construct the full domain URL from the current location
	const hostname = window.location.hostname;
	const protocol = window.location.protocol;

	return `${protocol}//${hostname}`;
}

// Fetches and caches location settings for the current domain
async function loadLocationSettings(queryClient: QueryClient) {
	return await queryClient.ensureQueryData(
		getDomainLocationSettingsQueryOptions(getDomain()),
	);
}

// Root layout: outlet, location setup, and Intercom guest chat only on unauthenticated paths
function RootRouteComponent() {
	const pathname = useRouterState({ select: (s) => s.location.pathname });
	const isAuthenticatedRoute =
		pathname.includes("/main") || pathname.includes("/alt");
	return (
		<MaintenanceGate
			enabled={MAINTENANCE_MODE_ENABLED}
			bypassPassword={MAINTENANCE_BYPASS_PASSWORD}
		>
			<>
				<Outlet />
				<LocationSettingsSetup />
				{!isAuthenticatedRoute && <IntercomChatGuest />}
			</>
		</MaintenanceGate>
	);
}

function RouterErrorComponent({
	error,
	reset,
}: {
	error: Error;
	reset: () => void;
}) {
	return <ErrorFallback error={error} resetErrorBoundary={reset} />;
}

// Create a root route
const rootRoute = createRootRouteWithContext<{ queryClient: QueryClient }>()({
	component: RootRouteComponent,
	pendingComponent: LoadingScreen,
	errorComponent: RouterErrorComponent,
	loader: async ({ context: { queryClient } }) => {
		const existingSettings = getLocationSettingsFromStorage();

		// Check if we have valid settings in local storage already
		const parsedSettings = LocationByDomainSchema.safeParse(existingSettings);

		if (parsedSettings.success) {
			setLocationIdForRequests(parsedSettings.data.location_id);

			// set query client data with valid local storage data.
			queryClient.setQueryData(
				locationSettingsKeys.locationSettingsByDomain(getDomain()),
				parsedSettings.data,
			);
			applyApplicationTheme(parsedSettings.data);

			// fetch the location settings from the server and set the local storage data.
			// This ensures that we are using the latest data from the server.
			// But we just update it in the background so we can use the local storage data immediately.
			queryClient
				.fetchQuery(getDomainLocationSettingsQueryOptions(getDomain()))
				.then((data) => {
					setLocationSettingsInStorage(data);
				});
			return parsedSettings.data;
		}
		clearLocationSettingsFromStorage();
		// No valid cached data - fetch synchronously
		const locationSettings = await queryClient.ensureQueryData(
			getDomainLocationSettingsQueryOptions(getDomain()),
		);

		setLocationSettingsInStorage(locationSettings);
		setLocationIdForRequests(locationSettings.location_id);

		applyApplicationTheme(locationSettings);

		return locationSettings;
	},
});

// Public routes (direct children of root; IntercomChatGuest shown via IntercomGuestOrNothing in root)
const indexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/",
	validateSearch: validatePromoCodeSearch,
	component: () => (
		<>
			<WelcomePage />
		</>
	),
});

const faceIdRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/face-id",
	component: FaceIDPage,
});

const signPinRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/sign-pin",
	component: SignPinPage,
});

const faceSignInRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/face-auth",
	component: FaceSignInPage,
	loader: () => {
		if (ENV.VITE_FACE_AUTH !== "true") {
			toast({
				title: "Face ID is not enabled",
				description: "Please use email login instead.",
				variant: "destructive",
			});
			throw redirect({ to: "/sign-mail" });
		}
	},
});

const signMailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/sign-mail",
	component: SignInMailPage,
});

const passwordRecoveryRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/password-recovery",
	component: PasswordRecoveryPage,
});

const resetPasswordRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/reset-password",
	validateSearch: (search: Record<string, unknown>) => {
		return {
			token: search.token as string,
		};
	},
	component: ResetPasswordPage,
});

// Email verification - public route (no auth required for clicking link in email)
const verifyEmailRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/verify-email",
	validateSearch: (search: Record<string, unknown>) => ({
		token: search.token as string | undefined,
	}),
	component: EmailVerificationPage,
});

const loadingRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/loading",
	component: LoadingPage,
});

const welcomeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/welcome",
	component: WelcomePage,
});

const welcomeMessageRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/welcome-message",
	validateSearch: (search: PromoCodeSearch) => validatePromoCodeSearch(search),
	component: WelcomeMessagePage,
});

// Sign up routes
const signUpAuthenticationRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/sign_up",
	loader: async ({ context: { queryClient } }) => {
		try {
			await loadLocationSettings(queryClient);
			ensureFreshToken(queryClient);
			return await queryClient.fetchQuery(getUserInformationOptions());
		} catch {
			return handleAuthError();
		}
	},
	component: () => (
		<PageTransition>
			<SignUpAuthWrapper>
				<Outlet />
			</SignUpAuthWrapper>
		</PageTransition>
	),
});

const signUpIndexRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/sign-up",
	validateSearch: (search) => ({
		promo: typeof search?.promo === "string" ? search.promo : undefined,
	}),
	component: SignUpWrapper,
});

const verifyAccountSearchSchema = z.object({
	intent: z.enum(["sign_up", "returning"]),
});

type VerifyAccountSearch = z.infer<typeof verifyAccountSearchSchema>;

const verifyAccountRoute = createRoute({
	getParentRoute: () => signUpAuthenticationRoute,
	path: "verify-account",
	validateSearch: (search: unknown): VerifyAccountSearch => {
		const result = verifyAccountSearchSchema.safeParse(search);
		return result.success ? result.data : { intent: "returning" };
	},
	component: () => <Outlet />,
});

const verifyAccountIndexRoute = createRoute({
	getParentRoute: () => verifyAccountRoute,
	path: "/",
	component: VerifyAccountPage,
});

const AuthenticatedRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/alt",
	loader: async ({ context: { queryClient } }) => {
		try {
			await loadLocationSettings(queryClient);
			await ensureFreshToken(queryClient);

			return await queryClient.ensureQueryData(getUserInformationOptions());
		} catch {
			return handleAuthError();
		}
	},
	component: () => (
		<AuthWrapper from="/alt">
			<PageTransition>
				<Outlet />
			</PageTransition>
		</AuthWrapper>
	),
});

// Main authenticated routes
export const authenticatedGuestRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/main",
	loader: async ({ context: { queryClient } }) => {
		try {
			const location = await loadLocationSettings(queryClient);
			await ensureFreshToken(queryClient);
			const user = await queryClient.ensureQueryData(
				getUserInformationOptions(),
			);

			const [creditCards, bankAccounts, transactionsData] = await Promise.all([
				queryClient.ensureQueryData(
					creditCardsQueryOptions({
						guestId: user.id,
						locationId: location.location_id,
					}),
				),
				queryClient.ensureQueryData(
					bankAccountsQueryOptions({
						guestId: user.id,
						locationId: location.location_id,
					}),
				),
				queryClient.ensureQueryData(
					transactionsQueryOptions({
						guestId: user.id,
						locationId: location.location_id,
						per_page: 100,
						page: 1,
						sort_by: "date",
						sort_order: "desc",
					}),
				),
			]);

			return { user, creditCards, bankAccounts, transactionsData };
		} catch {
			return handleAuthError();
		}
	},
	component: () => (
		<AuthWrapper>
			<AuthenticatedGuestLayout>
				<PageTransition>
					<UserAccountSetupGuard />
				</PageTransition>
			</AuthenticatedGuestLayout>
			<IntercomUserChat />
			<ResumePendingTransferIn />
			<ClearSessionErrorCheck />
		</AuthWrapper>
	),
});

const homeRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "home",
	component: HomeOverviewPage,
});

const homeVerifiedRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "home/verified",
	component: VerifiedPage,
});

const onboardingRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "onboarding",
	component: () => (
		<UserVerifiedAuthGuard>
			<OnboardingPage />
		</UserVerifiedAuthGuard>
	),
});

const verifyIdentityRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "verify-identity",
	component: VerifyIdentityPage,
});

const validateVerificationRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "validate-identity",
	component: ValidateVerificationPage,
});

const cardRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "card",
	component: CardViewPage,
});

const cardViewRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "card/view",
	component: CardViewTestPage,
});

const activityRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "activity",
	component: ActivityPage,
});

const settingRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "setting",
	loader: ({ context: { queryClient } }) => {
		// Non-blocking: kick off cache fill; do not await (avoid blocking navigation).
		void queryClient.prefetchQuery(getStatesQueryOptions(US_STATES_ID));
	},
	component: () => <Outlet />,
});
const settingIndexRoute = createRoute({
	getParentRoute: () => settingRoute,
	path: "/",
	component: SettingPage,
});

export const linkedAccountsRoute = createRoute({
	getParentRoute: () => settingRoute,
	path: "linked-accounts",
	component: LinkedAccountsPage,
});

export const verifyLinkedAccountRoute = createRoute({
	getParentRoute: () => settingRoute,
	path: "linked-accounts/new/verify",
	component: VerifyBankLinkingPage,
});

export const newAccountRoute = createRoute({
	getParentRoute: () => settingRoute,
	path: "linked-accounts/new",
	component: NewAccountPage,
});

const personalInfoSettingRoute = createRoute({
	getParentRoute: () => settingRoute,
	path: "personal_info",
	component: PersonalInfoSettingsPage,
});

export const businessDetailsSettingRoute = createRoute({
	getParentRoute: () => settingRoute,
	path: "business-details",
	loader: async ({ context: { queryClient } }) => {
		const locationSettings = await loadLocationSettings(queryClient);
		setLocationIdForRequests(locationSettings.location_id);
		const [user, states] = await Promise.all([
			// Always network-fetch user here so `guest_business_details` matches the server
			// after create/update (child loaders are not the only consumer — `AuthWrapper`
			// reads `user` from the `/main` parent loader).
			queryClient.fetchQuery(getUserInformationOptions()),
			queryClient.ensureQueryData(getStatesQueryOptions(US_STATES_ID)),
		]);
		return {
			guestBusinessDetails: user.guest_business_details,
			states,
		};
	},
	component: () => <Outlet />,
});

const businessDetailsIndexRoute = createRoute({
	getParentRoute: () => businessDetailsSettingRoute,
	path: "/",
	component: BusinessDetailsIndexPage,
});

export const depositRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "deposit",
	loader: async ({ context: { queryClient } }) => {
		const location = await loadLocationSettings(queryClient);
		const [signed, eventTypes] = await Promise.all([
			queryClient.ensureQueryData(
				guestSignatureStatusQueryOptions(location.location_id),
			),
			queryClient.ensureQueryData(
				documentSigningEventTypesQueryOptions(location.location_id),
			),
		]);
		return {
			signed: signed.signed,
			eventTypes,
		};
	},
	component: () => {
		return (
			<UserVerifiedAuthGuard>
				<UserAgreementGate>
					<Outlet />
				</UserAgreementGate>
			</UserVerifiedAuthGuard>
		);
	},
});

export const businessDetailsCreateRoute = createRoute({
	getParentRoute: () => businessDetailsSettingRoute,
	path: "create",
	// `route.to` is only correct after the route tree exists; do not capture it
	// in a module-level Zod schema or `z.literal` becomes `undefined`.
	validateSearch: (search) => {
		return z
			.object({
				callback: z
					.union([z.literal(newAccountRoute.to), z.literal(depositRoute.to)])
					.optional(),
			})
			.parse(search);
	},
	component: BusinessDetailsCreatePage,
});

const businessDetailsEditRoute = createRoute({
	getParentRoute: () => businessDetailsSettingRoute,
	path: "edit/$businessDetailId",
	component: BusinessDetailsEditPage,
});

const securitySettingRoute = createRoute({
	getParentRoute: () => settingRoute,
	path: "security",
	component: () => (
		<SecuritySettingsLayout>
			<Outlet />
		</SecuritySettingsLayout>
	),
});

const securitySettingsIndexRoute = createRoute({
	getParentRoute: () => securitySettingRoute,
	path: "/",
	component: SecurityPage,
});

const changePasswordRoute = createRoute({
	getParentRoute: () => securitySettingRoute,
	path: "change-password",
	component: ChangePasswordPage,
});

const securityChecklistRoute = createRoute({
	getParentRoute: () => securitySettingRoute,
	path: "checklist",
	component: SecurityChecklistPage,
});

const changePinRoute = createRoute({
	getParentRoute: () => securitySettingRoute,
	path: "change-pin",
	component: ChangePinPage,
});

const cardNewRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "card/new",
	component: () => (
		<UserVerifiedAuthGuard>
			<NewCardPage />
		</UserVerifiedAuthGuard>
	),
	loader: async ({ context: { queryClient } }) => {
		const location = await loadLocationSettings(queryClient);
		return queryClient.ensureQueryData(
			paymentCredentialsOptions(location.location_id),
		);
	},
});

const depositIndexRoute = createRoute({
	getParentRoute: () => depositRoute,
	path: "/",
	component: DepositPage,
});

const depositAmountRoute = createRoute({
	getParentRoute: () => depositRoute,
	path: "$cardId/amount",
	loader: async ({ context: { queryClient } }) => {
		const location = await loadLocationSettings(queryClient);

		return queryClient.ensureQueryData(
			discountCodeByUserQuery(location.location_id),
		);
	},
	component: DepositAmountPage,
});

const withdrawRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "withdraw",
	component: () => {
		return (
			<UserVerifiedAuthGuard>
				<WithdrawPage />
			</UserVerifiedAuthGuard>
		);
	},
});

const withdrawAmountRoute = createRoute({
	getParentRoute: () => authenticatedGuestRoute,
	path: "withdraw/$accountId/amount",
	component: () => {
		return (
			<UserVerifiedAuthGuard>
				<WithdrawAmountPage />
			</UserVerifiedAuthGuard>
		);
	},
});

// const referredFriendsRoute = createRoute({
// 	getParentRoute: () => settingRoute,
// 	path: "referred-friends",
// 	component: ReferredFriendsPage,
// });

const VerifyPhoneCodeSchema = z.object({
	phone: z.string().default(""),
	country: z.number(),
	log_id: z.number().optional(),
	candidate_ids: z.array(z.number()).optional(),
});
// Enter code (public)
const verifyPhoneCodeRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "/verify-code",
	validateSearch: (search) => VerifyPhoneCodeSchema.parse(search),
	component: VerifyPhoneCodePage,
});

// 404 route
const notFoundRoute = createRoute({
	getParentRoute: () => rootRoute,
	path: "*",
	component: NotFound,
});

const faceRegistrationRoute = createRoute({
	getParentRoute: () => AuthenticatedRoute,
	path: "face-registration",
	component: FaceRegistration,
});

// Create the route tree
const routeTree = rootRoute.addChildren([
	indexRoute,
	faceIdRoute,
	signPinRoute,
	signMailRoute,
	passwordRecoveryRoute,
	resetPasswordRoute,
	verifyEmailRoute,
	loadingRoute,
	welcomeRoute,
	welcomeMessageRoute,
	verifyPhoneCodeRoute,
	signUpIndexRoute,
	signUpAuthenticationRoute.addChildren([
		verifyAccountRoute.addChildren([verifyAccountIndexRoute]),
	]),
	faceSignInRoute,
	authenticatedGuestRoute.addChildren([
		homeRoute,
		homeVerifiedRoute,
		onboardingRoute,
		verifyIdentityRoute,
		validateVerificationRoute,
		cardRoute,
		cardViewRoute,
		activityRoute,
		cardNewRoute,
		depositRoute.addChildren([depositIndexRoute, depositAmountRoute]),
		withdrawRoute,
		withdrawAmountRoute,
		// referredFriendsRoute,
		settingRoute.addChildren([
			changePasswordRoute,
			linkedAccountsRoute,
			verifyLinkedAccountRoute,
			newAccountRoute,
			personalInfoSettingRoute,
			businessDetailsSettingRoute.addChildren([
				businessDetailsIndexRoute,
				businessDetailsCreateRoute,
				businessDetailsEditRoute,
			]),
			// referredFriendsRoute,
			settingIndexRoute,
			securitySettingRoute.addChildren([
				securityChecklistRoute,
				changePasswordRoute,
				securitySettingsIndexRoute,
				changePinRoute,
			]),
		]),
	]),
	AuthenticatedRoute.addChildren([faceRegistrationRoute]),

	notFoundRoute,
]);

const basepath = import.meta.env.VITE_BASE_PATH || "/";

// Create the router
export const router = createRouter({
	routeTree,
	basepath,
	context: { queryClient },
	defaultPendingComponent: LoadingScreen,
	defaultErrorComponent: RouterErrorComponent,
	defaultPendingMs: 1000,
});

// Export type-safe route helpers
export const routes = {
	public: {
		home: indexRoute,
		faceId: faceIdRoute,
		signPin: signPinRoute,
		signMail: signMailRoute,
		passwordRecovery: passwordRecoveryRoute,
		resetPassword: resetPasswordRoute,
		verifyEmail: verifyEmailRoute,
		loading: loadingRoute,
		welcome: welcomeRoute,
		welcomeMessage: welcomeMessageRoute,
		faceSignIn: faceSignInRoute,
		verifyPhoneCode: verifyPhoneCodeRoute,
	},
	signUp: {
		root: signUpAuthenticationRoute,
		index: signUpIndexRoute,
		verifyAccount: verifyAccountRoute,
	},
	main: {
		home: homeRoute,
		homeVerified: homeVerifiedRoute,
		onboarding: onboardingRoute,
		verifyIdentity: verifyIdentityRoute,
		validateVerification: validateVerificationRoute,
		card: cardRoute,
		cardView: cardViewRoute,
		activity: activityRoute,
		setting: {
			personalInfo: personalInfoSettingRoute,
			businessDetails: businessDetailsSettingRoute,
			businessDetailsCreate: businessDetailsCreateRoute,
			businessDetailsEdit: businessDetailsEditRoute,
		},
		cardNew: cardNewRoute,
		deposit: depositRoute,
		depositAmount: depositAmountRoute,
		withdraw: withdrawRoute,
		withdrawAmount: withdrawAmountRoute,
	},
	alt: {
		selfieRegistration: faceRegistrationRoute,
	},
} as const;

// Register the router for type safety
declare module "@tanstack/react-router" {
	interface Register {
		router: typeof router;
	}
}
